/**
 * useHealthReportPublications — Issue #177.
 *
 * Admin-side hook for the reviewed health report publication queue.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion 走乐观锁。
 *   - 错误以 ApiError 转换；不静默吞掉 HTTP 错误。
 *   - 不在前端伪造已发布/已撤回的状态；mutation 必须使用后端返回值刷新本地状态。
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 健康报告发布审核端点都受 RBAC + 租户隔离保护；typed client 必须通过
// 共享的 tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type HealthReportPublicationAdminDto =
  ApiComponents['schemas']['HealthReportPublicationAdminDto'];
export type CreateHealthReportPublicationDto =
  ApiComponents['schemas']['CreateHealthReportPublicationDto'];
export type AdminReviewHealthReportPublicationDto =
  ApiComponents['schemas']['AdminReviewHealthReportPublicationDto'];
export type AdminPublishHealthReportPublicationDto =
  ApiComponents['schemas']['AdminPublishHealthReportPublicationDto'];
export type AdminRetractHealthReportPublicationDto =
  ApiComponents['schemas']['AdminRetractHealthReportPublicationDto'];
export type HealthReportPublicationEventDto =
  ApiComponents['schemas']['HealthReportPublicationEventDto'];

export type HealthReportPublicationState =
  HealthReportPublicationAdminDto['reviewState'];
export type HealthReportPublicationKind =
  HealthReportPublicationAdminDto['reportKind'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不一致，请刷新后重试',
      code: 'CONFLICT',
    };
  }
  if (status === 403) {
    return { message: '没有权限执行此操作或来源审稿已撤销', code: 'FORBIDDEN' };
  }
  if (status === 400) {
    return { message: '请求被拒绝，请检查来源审稿是否已通过', code: 'BAD_REQUEST' };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export interface ListFilter {
  reviewState?: HealthReportPublicationState;
  reportKind?: HealthReportPublicationKind;
  customerId?: string;
  serviceUserId?: string;
}

export function useHealthReportPublications() {
  const [publications, setPublications] = useState<
    HealthReportPublicationAdminDto[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPublications = useCallback(async (filter?: ListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = {};
      if (filter?.reviewState) query.reviewState = filter.reviewState;
      if (filter?.reportKind) query.reportKind = filter.reportKind;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.serviceUserId) query.serviceUserId = filter.serviceUserId;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/health-report-publications',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setPublications([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const createPublication = useCallback(
    async (
      dto: CreateHealthReportPublicationDto,
    ): Promise<HealthReportPublicationAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/health-report-publications',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const reviewPublication = useCallback(
    async (
      id: string,
      dto: AdminReviewHealthReportPublicationDto,
    ): Promise<HealthReportPublicationAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/health-report-publications/{id}/review',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const publishPublication = useCallback(
    async (
      id: string,
      dto: AdminPublishHealthReportPublicationDto,
    ): Promise<HealthReportPublicationAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/health-report-publications/{id}/publish',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const retractPublication = useCallback(
    async (
      id: string,
      dto: AdminRetractHealthReportPublicationDto,
    ): Promise<HealthReportPublicationAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/health-report-publications/{id}/retract',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const fetchEvents = useCallback(
    async (id: string): Promise<HealthReportPublicationEventDto[] | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/health-report-publications/{id}/events',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ? [...data] : null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  return {
    publications,
    loading,
    error,
    fetchPublications,
    createPublication,
    reviewPublication,
    publishPublication,
    retractPublication,
    fetchEvents,
  };
}
